import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { useAuthStore } from './stores/authStore';
import { SessionExpiredError, refreshAccessToken, secondsLeft, sharedToken, startProactiveRefresh } from './lib/authRefresh';
import { routeTree } from './routeTree.gen';
import './globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

async function initAuth() {
  // Reuse a still-valid token — this tab's own, or one another tab obtained —
  // instead of rotating the shared refresh cookie on every page load or new
  // tab (which is what raced on rapid F5 and when opening several tabs).
  const stored = sessionStorage.getItem('kp_at');
  const usable = [stored, sharedToken()].find((t) => secondsLeft(t) > 30);
  if (usable) {
    useAuthStore.getState().setAuth(usable);
    return;
  }

  try {
    await refreshAccessToken();
  } catch (err) {
    if (err instanceof SessionExpiredError || !stored) {
      useAuthStore.getState().clearAuth();
    } else {
      // Transient failure (API restarting, slow network): stay signed in with
      // the expired token; the first request 401s and the interceptor retries
      // the refresh. Clearing here used to log people out on a slow reload.
      useAuthStore.getState().setAuth(stored);
    }
  }
}

initAuth().then(() => {
  startProactiveRefresh();
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.StrictMode>
  );
});
